import { Button, Dialog, IconButton, Text, Tooltip } from '@radix-ui/themes';
import { PaperclipIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaginatedTemplateTable } from './TemplatePaginatedTable';
import { type AddGroupResourcesProps } from './types/addGroupResourceProps';
import { QuestionPaginatedTable } from './QuestionPaginatedTable';
import SingleSelect from '../../../elements/Select';

const AddGroupResources = ({ onSubmit, accessGroup, loading, disable }: AddGroupResourcesProps) => {
  const { t } = useTranslation();
  const [selectedResourceType, setSelectedResourceType] = useState<string | undefined>(undefined);

  const [selectedTemplateIds, setSelectedTemplateIds] = useState<{ resourceId: string; type: string }[]>(
    accessGroup.resources
      .filter((m) => m.__typename === 'Templatev2')
      .map((x) => ({
        resourceId: x.id,
        type: 'template',
      })),
  );
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<{ resourceId: string; type: string }[]>(
    accessGroup.resources
      .filter((m) => m.__typename === 'Questionv2')
      .map((x) => ({
        resourceId: x.id,
        type: 'question',
      })),
  );
  const handleSubmit = () => {
    onSubmit(accessGroup.id, [...selectedTemplateIds, ...selectedQuestionIds]);
  };
  const resourceOptions = [
    { label: t('settings.accessGroupv2.addGroupResources.resourceTypeQuestion'), value: 'question' },
    { label: t('settings.accessGroupv2.addGroupResources.resourceTypeTemplate'), value: 'template' },
  ];
  return (
    <Dialog.Root onOpenChange={() => {}}>
      <Tooltip
        content={
          <Text size="3">
            {disable
              ? t('settings.accessGroupv2.addGroupResources.noPermissionTooltip')
              : t('settings.accessGroupv2.addGroupResources.manageResourcesTooltip')}
          </Text>
        }
      >
        <Dialog.Trigger>
          <IconButton variant="ghost" color="gray" highContrast className="m-0" disabled={disable}>
            <PaperclipIcon size={20} />
          </IconButton>
        </Dialog.Trigger>
      </Tooltip>
      <Dialog.Content
        aria-describedby={undefined}
        style={{
          width: '1000px',
          maxWidth: '90vw',
        }}
      >
        <Dialog.Title>
          {selectedResourceType === 'question'
            ? t('settings.accessGroupv2.addGroupResources.dialogTitleQuestions')
            : t('settings.accessGroupv2.addGroupResources.dialogTitleTemplates')}
        </Dialog.Title>
        <div className="flex flex-col gap-2">
          <div className="flex flex-row justify-start items-center gap-3 pt-2">
            <div className="text-sm font-medium">{t('settings.accessGroupv2.addGroupResources.resourceTypeLabel')}</div>
            <SingleSelect
              options={resourceOptions}
              onChange={(value) => setSelectedResourceType(value)}
              placeholder={t('settings.accessGroupv2.addGroupResources.resourceTypePlaceholder')}
              className="w-1/4 my-6"
            />
          </div>

          <div className="pt-4">
            {selectedResourceType === 'question' && (
              <QuestionPaginatedTable setQuestionsIdsToSave={setSelectedQuestionIds} idExisting={selectedQuestionIds} />
            )}
            {selectedResourceType === 'template' && (
              <PaginatedTemplateTable setTemplateIdsToSave={setSelectedTemplateIds} idExisting={selectedTemplateIds} />
            )}
          </div>

          <div className="flex flex-row justify-between w-full pt-2">
            <Dialog.Close>
              <Button size="3" color="gray" variant="soft" className="w-full">
                {t('settings.accessGroupv2.addGroupResources.cancelButton')}
              </Button>
            </Dialog.Close>
            <Dialog.Close>
              <Button size="3" className="w-full" disabled={loading} loading={loading} onClick={handleSubmit}>
                {t('settings.accessGroupv2.addGroupResources.updateButton')}
              </Button>
            </Dialog.Close>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AddGroupResources;
